<template>
  <b-modal
    :id="modalId"
    centered
    :title="data?.status === 'in review' ? 'Update Activity' : 'Review Activity'"
    @hide="$emit('close')"
    hide-header-close
    @show="resetModal"
    @hidden="onHidden"
    @ok.prevent="handleOk"
    size="lg"
    :hide-footer="data?.status !== 'in review'"
  >
    <div class="p-2 d-flex flex-column gap-4">
      <!-- Course Info -->
      <b-row class="d-flex flex-column rounded bg-dark-soft p-4 gap-3">
        <b-col class="d-flex flex-column gap-1 col-12">
          <label>Task</label>
          <h4>{{ data?.task }}</h4>
        </b-col>

        <b-row class="d-flex flex-row justify-content-between align-items-center">
          <b-col class="d-flex flex-column gap-1 col-4">
            <label>Start Date</label>
            <h4>{{ formatDate(data?.start_date) }}</h4>
          </b-col>

          <b-col class="d-flex flex-column gap-1 col-4">
            <label>End Date</label>
            <h4>{{ formatDate(data?.end_date) }}</h4>
          </b-col>

          <b-col class="d-flex flex-column gap-1 col-4">
            <label>Percentage</label>
            <h4>{{ data?.percentage }}%</h4>
          </b-col>
        </b-row>

        <b-row class="d-flex flex-row align-items-center">
          <b-col v-if="data?.actual_start_date" class="d-flex flex-column gap-1 col-4">
            <label>Actual Start Date</label>
            <h4>{{ formatDate(data?.actual_start_date) }}</h4>
          </b-col>

          <b-col v-if="data?.actual_end_date" class="d-flex flex-column gap-1 col-4">
            <label>Actual End Date</label>
            <h4>{{ formatDate(data?.actual_end_date) }}</h4>
          </b-col>

          <b-col class="d-flex flex-column gap-0 col-4">
            <label>Status</label>
            <span :class="getTrainingStatusClass(data?.status)" class="mr-auto">
              {{ snakeToTitle(data?.status) }}
            </span>
          </b-col>
        </b-row>

        <b-col class="d-flex flex-column gap-1 col-4">
          <label>Assignee</label>
          <ul
            v-if="data?.employees?.length"
            class="list-unstyled mb-0"
            v-for="(employee, index) in data?.employees"
            :key="index"
          >
            <h4>{{ employee?.name }}</h4>
          </ul>
        </b-col>
      </b-row>

      <b-form ref="form" @submit.stop.prevent="handleSubmit">
        <b-form-group label="Progress Task">
          <vue-good-table
            :columns="progressTaskColumns"
            :rows="data?.progress"
            style-class="vgt-table border-0 table-auto"
            styleClass="vgt-table condensed"
          >
            <div slot="emptystate" class="text-center font-size-14">No data available</div>

            <template slot="table-row" slot-scope="props">
              <span v-if="props.column.field === 'date'">
                <div class="d-flex flex-row align-items-center justify-content-start">
                  <span>{{ formatDate(props.row.date) }}</span>
                </div>
              </span>

              <span v-else-if="props.column.field === 'attachments'">
                <div class="d-flex flex-row align-items-center justify-content-start">
                  <a v-if="props.row.attachments" :href="props.row.attachments" target="_blank">File</a>
                  <span v-else>No evidence available</span>
                </div>
              </span>

              <span v-else-if="props.column.field === 'progress'">
                <div class="d-flex flex-row align-items-center justify-content-start">
                  <span>{{ props.row.progress || 0 }}%</span>
                </div>
              </span>

              <span v-else class="d-flex align-items-center justify-content-start">
                {{ props.formattedRow[props.column.field] }}
              </span>
            </template>
          </vue-good-table></b-form-group
        >

        <b-form-group label="Progress" disabled>
          <div class="d-flex align-items-center gap-2">
            <b-form-input
              :value="data?.count_progress_activity"
              type="range"
              min="0"
              max="100"
              step="1"
              class="flex-grow-1"
              disabled
            />
            <div class="progress-value ms-2">{{ data?.count_progress_activity || 0 }}%</div>
          </div>
        </b-form-group>

        <b-form-group v-if="data?.evidence" label="Evidence">
          <div class="d-flex flex-column gap-1 w-75">
            <a v-if="data?.evidence" class="font-weight-bold" :href="data?.evidence" target="_blank">Evidence File</a>
          </div>
        </b-form-group>

        <b-form-group
          v-if="data?.status === 'in review' || data?.status === 'completed'"
          label="Feedback"
          :state="getState('feedback')"
          invalid-feedback="Feedback is required."
        >
          <h4 v-if="data?.feedback?.feedback">{{ data?.feedback?.feedback }}</h4>
          <b-form-textarea v-else rows="2" v-model="form.feedback" placeholder="Feedback" :state="getState('feedback')" />
        </b-form-group>
      </b-form>
    </div>

    <template #modal-footer>
      <b-button variant="primary" @click="handleOk" :disabled="isLoading" :key="isLoading">
        <b-spinner v-if="isLoading" small type="grow" class="me-2"></b-spinner>
        <span>{{ isLoading ? "Submitting..." : "Submit" }}</span>
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { formatDate, formatTimeToHHMM, isPdfFile, getTrainingStatusClass, snakeToTitle, cleanObject } from "../utils/utils";

export default {
  name: "UpdateActivityModal",

  props: {
    modalId: { type: String, default: "update-activity-modal" },
    data: { type: Object, default: null },
    isLoading: { type: Boolean, default: false },
  },

  data() {
    return {
      form: this.getInitialForm(),
      selectedFile: null,
      previewUrl: null,
      isPdf: false,
      isDragging: false,
      submitted: false,
      formErrors: {},
      suppressValidation: false,
      progressTaskColumns: [
        {
          label: "Name",
          field: "name",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Date",
          field: "date",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Progress",
          field: "progress",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
        {
          label: "Attachment",
          field: "attachments",
          sortable: false,
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary",
        },
      ],
    };
  },

  methods: {
    formatTimeToHHMM,
    isPdfFile,
    formatDate,
    getTrainingStatusClass,
    snakeToTitle,

    clearError(field) {
      if (this.formErrors[field]) {
        this.$delete(this.formErrors, field);
      }
    },

    getState(field) {
      if (this.formErrors[field]) return false;
      return null;
    },

    onHidden() {
      this.resetModal();
      this.$emit("close");
    },

    handleOk(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.handleSubmit();
    },

    validateAll() {
      this.formErrors = {};

      if (!this.form.feedback) this.formErrors.feedback = "Feedback is required.";

      return Object.keys(this.formErrors).length === 0;
    },

    handleSubmit() {
      const isValid = this.validateAll();
      if (!isValid) return;

      const payload = { ...this.form };

      this.$emit("submit-feedback", cleanObject(payload));
    },

    getInitialForm() {
      return {
        feedback: null,
        id: this.data?.id,
      };
    },

    async resetModal() {
      if (this.isLoading) return;

      this.suppressValidation = true;

      await this.$nextTick();

      this.form = this.getInitialForm();
      this.formErrors = {};
      this.selectedFile = null;
      this.previewUrl = null;

      this.$nextTick(() => {
        this.suppressValidation = false;
      });
    },
  },

  beforeDestroy() {},
};
</script>

<style scoped>
.dropzone {
  background-color: #f8f9fa;
  border: 2px dashed #dee2e6 !important;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dropzone:hover,
.dropzone.border-danger {
  border-color: #e63757 !important;
  background-color: #fff5f7;
}

.dropzone.is-invalid {
  border-color: #dc3545 !important;
}

.course-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #fce4e4;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gap-2 {
  gap: 0.5rem;
}

.me-1 {
  margin-right: 0.25rem;
}

.me-2 {
  margin-right: 0.5rem;
}

.me-3 {
  margin-right: 1rem;
}

/* PDF Preview Styles */
.pdf-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.pdf-preview .pdf-icon-container {
  width: 80px;
  height: 100px;
  background: linear-gradient(135deg, #e63757 0%, #c82333 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(230, 55, 87, 0.3);
}

.pdf-preview .pdf-icon-container i {
  font-size: 2.5rem;
  color: #fff;
}

.pdf-preview .pdf-label {
  margin-top: 1rem;
  font-weight: 600;
  color: #1a202c;
}

:deep(.table-header) {
  font-size: 11px !important;
}

:deep(.vgt-table td, .vgt-table th) {
  font-size: 11px !important;
}
</style>
